import { useState, useRef } from 'react'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { useProducts, useCreateProduct, useBrands, useSubscriptions, useUploadImage, useUpdateProduct, useDeleteProduct, useToggleProductStatus } from '../hooks/useApi'
import { Wand2, Package, Tag, Upload, X, Edit2, Check, Trash2, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'

import { getProductLimit, normalizePlanId, getPlanDisplayName, formatPlanLimit, isUnderPlanLimit, resolveProductLimit, canUseFeature } from '../lib/plans'
import { getImageUrl } from '../lib/catalogUrl'
import { ProductSeoScore } from '../components/ProductSeoScore'

const API_URL = import.meta.env.VITE_API_URL || '/api'

// Mock AI functions
const generateAIDescription = async (productName: string) => {
  await new Promise(resolve => setTimeout(resolve, 1500))
  const templates = [
    `${productName} - Produk berkualitas tinggi dengan harga terjangkau. Cocok untuk kebutuhan sehari-hari Anda. Stok terbatas, pesan sekarang!`,
    `Dapatkan ${productName} dengan kualitas premium. Original dan bergaransi. Produk favorit pelanggan kami!`,
    `Promo spesial ${productName}! Harga murah tapi kualitas tidak murahan. Gratis ongkir untuk area tertentu.`
  ]
  return templates[Math.floor(Math.random() * templates.length)]
}

export default function Products() {
  const { data: products, isLoading } = useProducts()
  const { data: brands } = useBrands()
  const { data: subscriptions } = useSubscriptions()
  const createProduct = useCreateProduct()
  const updateProduct = useUpdateProduct()
  const deleteProduct = useDeleteProduct()
  const toggleProductStatus = useToggleProductStatus()
  
  // Form states
  const [formData, setFormData] = useState({
    brandId: '',
    name: '',
    description: '',
    price: '',
    discountPrice: '',
    images: [] as string[]
  })
  const [isGenerating, setIsGenerating] = useState(false)
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Edit states
  const [editingProduct, setEditingProduct] = useState<any>(null)
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    price: '',
    discountPrice: '',
    images: [] as string[]
  })
  const [editUploadingIndex, setEditUploadingIndex] = useState<number | null>(null)
  const editFileInputRef = useRef<HTMLInputElement>(null)

  // Check subscription limits
  const currentSub = subscriptions?.[0]
  const isPlanActive = currentSub?.isActive !== false
  const currentPlan = normalizePlanId(isPlanActive ? currentSub?.plan : 'free')
  const currentPlanName = getPlanDisplayName(isPlanActive ? currentSub?.plan : 'free')
  const maxProducts = resolveProductLimit(isPlanActive ? currentSub?.productLimit : undefined, currentPlan)
  const selectedBrandId = formData.brandId || (brands?.length === 1 ? String(brands[0].id) : '')
  const productCount = selectedBrandId
    ? (products?.filter((p: { brandId: number }) => String(p.brandId) === selectedBrandId).length ?? 0)
    : (products?.length ?? 0)
  const canCreateProduct = isUnderPlanLimit(productCount, maxProducts)
  const maxImages = 10
  const canUseAI = currentPlan === 'premium'
  const hasProductSeo = canUseFeature(currentPlan, 'product_seo')

  const uploadImage = useUploadImage()

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    if (formData.images.length >= maxImages) {
      toast.error(`Maksimal ${maxImages} gambar per produk`)
      return
    }
    
    setUploadingIndex(formData.images.length)
    try {
      const result = await uploadImage.mutateAsync(file)
      setFormData({ ...formData, images: [...formData.images, result.url] })
    } catch (error) {
      console.error('Upload error:', error)
    } finally {
      setUploadingIndex(null)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleGenerateDescription = async () => {
    if (!formData.name || !canUseAI) return
    
    setIsGenerating(true)
    try {
      const description = await generateAIDescription(formData.name)
      setFormData({ ...formData, description })
      toast.success('Deskripsi AI berhasil dibuat! ✨')
    } catch (error) {
      toast.error('Gagal generate deskripsi')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canCreateProduct) {
      toast.error(`Paket ${currentPlanName} maksimal ${formatPlanLimit(maxProducts)} produk per katalog. Upgrade untuk menambah!`)
      return
    }
    await createProduct.mutateAsync({
      brandId: parseInt(formData.brandId),
      name: formData.name,
      description: formData.description,
      price: parseFloat(formData.price),
      discountPrice: formData.discountPrice ? parseFloat(formData.discountPrice) : null,
      images: formData.images,
      isActive: true
    })
    setFormData({ brandId: '', name: '', description: '', price: '', discountPrice: '', images: [] })
  }

  const handleEditClick = (product: any) => {
    setEditingProduct(product)
    setEditFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price?.toString() || '',
      discountPrice: product.discountPrice?.toString() || '',
      images: product.images || []
    })
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingProduct) return
    
    await updateProduct.mutateAsync({
      id: editingProduct.id,
      data: {
        name: editFormData.name,
        description: editFormData.description,
        price: parseFloat(editFormData.price),
        discountPrice: editFormData.discountPrice ? parseFloat(editFormData.discountPrice) : null,
        images: editFormData.images
      }
    })
    setEditingProduct(null)
  }

  const handleDeleteProduct = async (productId: number) => {
    if (confirm('Apakah Anda yakin ingin menghapus produk ini?')) {
      await deleteProduct.mutateAsync(productId)
    }
  }

  const handleToggleStatus = async (productId: number) => {
    await toggleProductStatus.mutateAsync(productId)
  }

  const handleEditFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    if (editFormData.images.length >= maxImages) {
      toast.error(`Maksimal ${maxImages} gambar per produk`)
      return
    }
    
    setEditUploadingIndex(editFormData.images.length)
    try {
      const result = await uploadImage.mutateAsync(file)
      setEditFormData({ ...editFormData, images: [...editFormData.images, result.url] })
    } catch (error) {
      console.error('Upload error:', error)
    } finally {
      setEditUploadingIndex(null)
      if (editFileInputRef.current) {
        editFileInputRef.current.value = ''
      }
    }
  }

  if (isLoading) return <div>Loading...</div>

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 flex flex-wrap items-center gap-2">
        <Package className="w-7 h-7 sm:w-8 sm:h-8 shrink-0" />
        Kelola Produk
      </h1>
      <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm sm:text-base">
        <span className="text-gray-600">Plan:</span>
        <span className="font-semibold text-blue-600">{currentPlanName}</span>
        <span className="hidden sm:inline text-gray-400">|</span>
        <span className="text-gray-600">Produk:</span>
        <span className={`font-semibold ${!canCreateProduct ? 'text-red-600' : 'text-green-600'}`}>
          {productCount} / {formatPlanLimit(maxProducts)}
          {selectedBrandId && brands && brands.length > 1 && (
            <span className="text-xs text-gray-500 w-full sm:w-auto">(per katalog)</span>
          )}
        </span>
        {!canCreateProduct && (
          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded inline-flex items-center gap-1 w-full sm:w-auto">
            <AlertCircle className="w-3 h-3 shrink-0" />
            Limit produk tercapai
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Create Product Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              Tambah Produk Baru
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand *
                </label>
                <select
                  className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2"
                  value={formData.brandId}
                  onChange={(e) => setFormData({...formData, brandId: e.target.value})}
                  required
                >
                  <option value="">Pilih Brand</option>
                  {brands?.map((brand: any) => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nama Produk *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Contoh: Kopi Gayo Premium"
                  required
                />
              </div>
              
              {/* Image Upload Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gambar Produk 
                  <span className="text-gray-500">({formData.images.length}/{maxImages})</span>
                </label>
                
                {/* File Input - Hidden */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*"
                  className="hidden"
                />
                
                {/* Upload Button & Preview */}
                <div className="flex gap-2 flex-wrap">
                  {formData.images.map((img, idx) => (
                    <div key={idx} className="relative">
                      <img 
                        src={getImageUrl(img) || ''} 
                        alt={`Product ${idx + 1}`}
                        className="w-20 h-20 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          images: formData.images.filter((_, i) => i !== idx)
                        })}
                        className="absolute -top-1 -right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  
                  {/* Add Image Button */}
                  {formData.images.length < maxImages && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingIndex !== null}
                      className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-blue-500 hover:text-blue-500 transition-colors disabled:opacity-50"
                    >
                      {uploadingIndex !== null ? (
                        <span className="text-xs">Uploading...</span>
                      ) : (
                        <>
                          <Upload className="w-6 h-6 mb-1" />
                          <span className="text-xs">Add</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                
                <p className="text-xs text-gray-500 mt-2">
                  Klik tombol + untuk upload gambar. Max 5MB per file (jpg, png, webp, gif).
                </p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Harga Normal *
                  </label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="100000"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Harga Promo (opsional)
                  </label>
                  <Input
                    type="number"
                    value={formData.discountPrice}
                    onChange={(e) => setFormData({...formData, discountPrice: e.target.value})}
                    placeholder="85000"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deskripsi Produk
                </label>
                <div className="relative">
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Jelaskan detail produk Anda..."
                    className="w-full px-3 py-2 pb-12 sm:pb-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                  />
                  {canUseAI && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateDescription}
                      disabled={!formData.name || isGenerating}
                      className="absolute bottom-2 right-2 text-xs sm:text-sm"
                    >
                      <Wand2 className="w-4 h-4 mr-1" />
                      {isGenerating ? 'Generating...' : 'AI Generate'}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {canUseAI 
                    ? 'Klik AI Generate untuk deskripsi otomatis yang menarik!' 
                    : 'Upgrade ke Premium untuk fitur AI Copywriting'}
                </p>
                <div className="mt-3">
                  <ProductSeoScore
                    name={formData.name}
                    description={formData.description}
                    images={formData.images}
                  />
                </div>
                {hasProductSeo && formData.name && (
                  <p className="text-xs text-emerald-700 mt-2">
                    Paket Premium: setiap produk mendapat halaman SEO sendiri di katalog publik.
                  </p>
                )}
              </div>

              {!canCreateProduct && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    Paket <strong>{currentPlanName}</strong> maksimal {formatPlanLimit(maxProducts)} produk per katalog.
                    {' '}
                    <a href="/subscription" className="text-blue-600 underline">Upgrade paket</a> untuk menambah produk.
                  </span>
                </div>
              )}
              
              <Button type="submit" className="w-full" disabled={formData.images.length === 0 || !canCreateProduct}>
                Simpan Produk
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Product List */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Produk Anda</h2>
          
          {/* Edit Product Modal */}
          {editingProduct && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Edit2 className="w-5 h-5" />
                    Edit Produk: {editingProduct.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleEditSubmit} className="space-y-4">
                    {/* Image Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Gambar Produk ({editFormData.images.length}/{maxImages})
                      </label>
                      <input
                        type="file"
                        ref={editFileInputRef}
                        onChange={handleEditFileSelect}
                        accept="image/*"
                        className="hidden"
                      />
                      <div className="flex gap-2 flex-wrap">
                        {editFormData.images.map((img, idx) => (
                          <div key={idx} className="relative">
                            <img 
                              src={getImageUrl(img) || ''} 
                              alt={`Product ${idx + 1}`}
                              className="w-20 h-20 object-cover rounded-lg border"
                            />
                            <button
                              type="button"
                              onClick={() => setEditFormData({
                                ...editFormData,
                                images: editFormData.images.filter((_, i) => i !== idx)
                              })}
                              className="absolute -top-1 -right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        {editFormData.images.length < maxImages && (
                          <button
                            type="button"
                            onClick={() => editFileInputRef.current?.click()}
                            disabled={editUploadingIndex !== null}
                            className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-blue-500 hover:text-blue-500 transition-colors disabled:opacity-50"
                          >
                            {editUploadingIndex !== null ? (
                              <span className="text-xs">...</span>
                            ) : (
                              <>
                                <Upload className="w-6 h-6 mb-1" />
                                <span className="text-xs">Add</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nama Produk *
                      </label>
                      <Input
                        value={editFormData.name}
                        onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                        placeholder="Contoh: Kopi Gayo Premium"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Harga Normal *
                        </label>
                        <Input
                          type="number"
                          value={editFormData.price}
                          onChange={(e) => setEditFormData({...editFormData, price: e.target.value})}
                          placeholder="100000"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Harga Promo (opsional)
                        </label>
                        <Input
                          type="number"
                          value={editFormData.discountPrice}
                          onChange={(e) => setEditFormData({...editFormData, discountPrice: e.target.value})}
                          placeholder="85000"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Deskripsi Produk
                      </label>
                      <textarea
                        value={editFormData.description}
                        onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                        placeholder="Jelaskan detail produk Anda..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={4}
                      />
                      <div className="mt-3">
                        <ProductSeoScore
                          name={editFormData.name}
                          description={editFormData.description}
                          images={editFormData.images}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row gap-2 pt-4">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setEditingProduct(null)}
                        disabled={updateProduct.isPending}
                        className="w-full sm:w-auto"
                      >
                        Batal
                      </Button>
                      <Button type="submit" className="flex-1 w-full sm:w-auto" disabled={updateProduct.isPending}>
                        <Check className="w-4 h-4 mr-2" />
                        {updateProduct.isPending ? 'Menyimpan...' : 'Simpan Perubahan'}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}
          
          {products?.map((product: any) => (
            <Card key={product.id} className={`hover:shadow-md transition-shadow ${!product.isActive ? 'opacity-75' : ''}`}>
              <CardContent className="pt-4 sm:pt-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  {product.images?.[0] && (
                    <img 
                      src={getImageUrl(product.images[0]) || ''} 
                      alt={product.name}
                      className="w-full sm:w-24 h-40 sm:h-24 object-cover rounded-lg shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-base sm:text-lg flex flex-wrap items-center gap-2">
                          <span className="break-words">{product.name}</span>
                          {!product.isActive && (
                            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded shrink-0">
                              Non-Aktif
                            </span>
                          )}
                        </h3>
                        <p className="text-gray-600 text-sm line-clamp-2 mt-1 break-words">{product.description}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(product.id)}
                          className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                            product.isActive 
                              ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title={product.isActive ? 'Klik untuk non-aktifkan' : 'Klik untuk aktifkan'}
                        >
                          {product.isActive ? 'Aktif' : 'Non-Aktif'}
                        </button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditClick(product)}
                          aria-label="Edit produk"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteProduct(product.id)}
                          aria-label="Hapus produk"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-3">
                      <span className="font-bold text-gray-900 text-sm sm:text-base">
                        Rp {product.price?.toLocaleString()}
                      </span>
                      {product.discountPrice && (
                        <span className="text-xs sm:text-sm text-green-600 bg-green-50 px-2 py-1 rounded">
                          Promo Rp {product.discountPrice?.toLocaleString()}
                        </span>
                      )}
                    </div>
                    {product.images?.length > 1 && (
                      <p className="text-xs text-gray-500 mt-1">
                        +{product.images.length - 1} gambar lainnya
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {products?.length === 0 && (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Belum ada produk. Tambahkan produk pertama Anda!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
